import { SingleTranscriptionDocumentStorageManager } from '../documentStorage/SingleTranscriptionDocumentStorageManager';
import { IStoredTranscriptionArray } from '../interfaces/IStoredTranscriptionArray';
import { IStoredTranscription, TranscriptionStorageType } from '../interfaces/IStoredTranscription';
import { ITranscriptionManager } from '../interfaces/ITranscriptionManager';
import { HostAppSettingName, HostAppSettings } from '../office-js/HostAppSettings';
import { CreateDocumentStorage } from './DocumentStorageFactory';
import { MultiTranscriptionDocumentStorageManager } from '../documentStorage/MultiTranscriptionDocumentStorageManager';
import { DocumentStorage } from '../office-js/DocumentStorage';
import { IncorrectStorageSchemaError, InCorrectStorageSchemaErrorName } from '../documentStorage/InCorrectStorageSchemaError';
import { ErrorStringFromException } from '../../utils/utils';

/*TRANSCRIPT_STORAGE_VERSION should be updated,
when there is any minor/major change in Schema for IStoredTransctiption or IStoredTransctionArray.*/
export const TRANSCRIPT_STORAGE_VERSION = 1.1;

export class TranscriptionManagerFactory {
  private static _instance: ITranscriptionManager;

  public static GetInstance(): ITranscriptionManager {
    if (this._instance == null) {
      this._instance = this.CreateTranscriptionManager();
    }
    return this._instance;
  }

  private static CreateTranscriptionManager(): ITranscriptionManager {
    const documentStorage = CreateDocumentStorage();

    //Case 1: No content associated with opened document Page.
    if (!HostAppSettings.IsSettingTrue(HostAppSettingName.HasStoredTranscription)) {
      return this.CreateTranscriptionManagerWithNoStoredTranscription(documentStorage);
    } else {
      return this.CreateTranscriptionManagerWithStoredTranscription(documentStorage);
    }
  }

  private static CreateTranscriptionManagerWithNoStoredTranscription(documentStorage: DocumentStorage) {
    if (HostAppSettings.IsSettingTrue(HostAppSettingName.IsMultiTranscriptionStorageSupported)) {
      return new MultiTranscriptionDocumentStorageManager(documentStorage, [],
        HostAppSettings.GetSettingAsNumber(HostAppSettingName.MultiTranscriptStorageLimitPerPage));
    } else {
      return new SingleTranscriptionDocumentStorageManager(documentStorage);
    }
  }

  private static CreateTranscriptionManagerWithStoredTranscription(documentStorage: DocumentStorage) {
    // Content associated with opened document Page.
    const storedTranscriptionJson = documentStorage.GetStoredTranscription();
    try {
      if (HostAppSettings.IsSettingTrue(HostAppSettingName.IsMultiTranscriptionStorageSupported)) {

        // case 2 : Multi Transcripts<IStoredTranscrptionArray> are stored on opened Document page.
        return this.CreateTranscriptionManagerWithMultiTranscriptionSchema(storedTranscriptionJson, documentStorage);
      } else {

        // case 3 : Single Transcript<IStoredTranscription> is stored on opened Document page.
        return this.CreateTranscriptionManagerWithSingleTranscriptionSchema(storedTranscriptionJson, documentStorage);
      }
    } catch (e: any) {

      if (e instanceof IncorrectStorageSchemaError) {

        /* case 4 : Backward compatibility handling, Transcription Content Schema and IsMultiTranscriptionStorageSupported
        Feature mismatch.*/
        return this.HandleIncorrectSchemaException(e, storedTranscriptionJson, documentStorage);
      }
      throw e;
    }
  }

  private static CreateTranscriptionManagerWithMultiTranscriptionSchema(storedTranscriptionArray: IStoredTranscriptionArray, documentStorage: DocumentStorage): ITranscriptionManager {

    /* TODO: version information {TRANSCRIPT_STORAGE_VERSION} will be used to differentiate
    b/w Various IStoredTranscript Schema.*/
    switch (storedTranscriptionArray.storageType) {
      case TranscriptionStorageType.DocumentStorage:
        try {
          return new MultiTranscriptionDocumentStorageManager(documentStorage, storedTranscriptionArray.storedTranscriptions, storedTranscriptionArray.version,
            HostAppSettings.GetSettingAsNumber(HostAppSettingName.MultiTranscriptStorageLimitPerPage));
        } catch (e) {
          throw new IncorrectStorageSchemaError(InCorrectStorageSchemaErrorName.InvalidWithIStoredTranscriptionArraySchema, ErrorStringFromException(e));
        }
      default:
        throw new Error('Unsupported transcription storage type: ' + storedTranscriptionArray.storageType);
    }
  }

  private static CreateTranscriptionManagerWithSingleTranscriptionSchema(storedTranscription: IStoredTranscription, documentStorage: DocumentStorage) {
    switch (storedTranscription.storageType) {
      case TranscriptionStorageType.DocumentStorage:
        try {
          return new SingleTranscriptionDocumentStorageManager(documentStorage, storedTranscription);
        } catch (e) {
          throw new IncorrectStorageSchemaError(InCorrectStorageSchemaErrorName.InvalidWithIStoredTranscriptionSchema, ErrorStringFromException(e));
        }
      default:
        throw new Error('Unsupported transcription storage type: ' + storedTranscription.storageType);
    }
  }

  private static HandleIncorrectSchemaException(e: IncorrectStorageSchemaError, storedTranscriptionJson: any, documentStorage: DocumentStorage): ITranscriptionManager {
    if (e.name == InCorrectStorageSchemaErrorName.InvalidWithIStoredTranscriptionArraySchema) {

      // 'IsMultiTranscriptionStorageSupported' is true but associated Schema is IStoredTranscrition
      const storedTranscription: IStoredTranscription = <IStoredTranscription>storedTranscriptionJson;

      const storedTranscriptionArr: IStoredTranscriptionArray = {
        version: TRANSCRIPT_STORAGE_VERSION,
        storageType: storedTranscription.storageType,
        storedTranscriptions: [storedTranscription]
      };

      return this.CreateTranscriptionManagerWithMultiTranscriptionSchema(storedTranscriptionArr, documentStorage);
    } else if (e.name == InCorrectStorageSchemaErrorName.InvalidWithIStoredTranscriptionSchema) {

      // 'IsMultiTranscriptionStorageSupported' is false but associated Schema is IStoredTranscritionArray
      const storedTranscriptionArr: IStoredTranscriptionArray = <IStoredTranscriptionArray>storedTranscriptionJson;

      /* Even if HostAppSetting 'IsMultiTranscriptionStorageSupported' is false, MultiDocumentStorageTranscriptionManager
      should be created to prevent User data loss and UX can show single topmost transcription content in case 'IsMultiTranscriptionStorageSupported'
      is disabled*/
      return this.CreateTranscriptionManagerWithMultiTranscriptionSchema(storedTranscriptionArr, documentStorage);
    } else {
      throw e;
    }
  }
}
